import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBook, FaFilePdf, FaClipboardList, FaBookOpen } from "react-icons/fa";
import styles from "../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import { CustomTextInput, CustomButton } from "../../../../components";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";
import QuillEditor from "../../../../components/QuillEditor/QuillEditor";
import SavedLessonPlanCard from "../../../../components/SavedLessonPlanCard/SavedLessonPlanCard";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import ScheduleLessonPlanContent from "./ScheduleLessonPlanContent";

const extractSubjectFromFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "";
  const base = fileName.replace(/\.pdf$/i, "");
  const parts = base.split("_");
  if (parts.length >= 3) {
    return parts[1]?.trim() || "";
  }
  return "";
};

const pickHmId = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  const candidates = [obj.hm_id, obj.hmId, obj.hm, obj.hm_user_id];
  for (const c of candidates) {
    if (c != null && c !== "" && !Number.isNaN(Number(c))) return Number(c);
  }
  return null;
};

// const formatYmd = (value) => {
//   if (!value) return "";
//   try {
//     const d = new Date(value);
//     if (Number.isNaN(d.getTime())) return "";
//     const y = d.getFullYear();
//     const m = String(d.getMonth() + 1).padStart(2, "0");
//     const day = String(d.getDate()).padStart(2, "0");
//     return `${y}-${m}-${day}`;
//   } catch (_) {
//     return "";
//   }
// };

// Safely turn a value into a Number or return null (never NaN / "").
const toNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const LessonPlanView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lessonPlanId = location.state?.ID;
  const academicId = location.state?.academicId;
  const fromPreviousYears = Boolean(location.state?.fromPreviousYears);

  const user = TokenService.getUser();
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_id = fromPreviousYears
    ? academicId || clientData?.academic_year_id || ""
    : clientData?.academic_year_id || "";
  const academic_id_for_completedDraft = clientData?.academic_year_id || "";
  const userId = user?.id != null ? Number(user.id) : null;
  // const staffId =
  //   clientData?.master_id != null && clientData?.master_id !== ""
  //     ? Number(clientData.master_id)
  //     : null;

  const [modal, setModal] = useState({
    open: false,
    type: "",
    message: "",
    navigateAfter: null,
  });

  // Confirm delete modal
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    planId: null,
  });

  // Schedule popup state
  const [schedulePopup, setSchedulePopup] = useState({
    open: false,
    lessonPlan: null,
  });

  // Loading
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lessonInfo, setLessonInfo] = useState(null);
  const [hmId, setHmId] = useState(null);
  const [headerData, setHeaderData] = useState({
    book: "",
    class: "",
    subject: "",
    medium: "",
    topic: "",
    subTopic: "",
    startPage: "",
    endPage: "",
  });

  // PDF
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fromPage, setFromPage] = useState(null);
  const [toPage, setToPage] = useState(null);

  // Lesson plan cards
  const [savedLessonPlans, setSavedLessonPlans] = useState([]);
  const [selectedLessonPlanId, setSelectedLessonPlanId] = useState(null);
  const [lessonPlanContent, setLessonPlanContent] = useState("");

  // Globally disable SCHEDULE on every card if ANY card has a final status
  const disableScheduleGlobally = useMemo(() => {
    return savedLessonPlans.some((p) => {
      const s = String(p.status || "").toLowerCase();
      return (
        s === "approved" ||
        s === "completed" ||
        s === "submitted" ||
        s === "resubmitted" ||
        s === "re-submitted"
      );
    });
  }, [savedLessonPlans]);

  // ---------- Fetch lesson plan from API ----------
  const fetchLessonPlan = useCallback(async () => {
    if (!lessonPlanId || !school_id) return;

    try {
      setLoading(true);

      // 👇 Previous Years flow uses the "completedView" endpoint.
      //    Everything else keeps the original "view" endpoint.
      const viewEndpoint = fromPreviousYears
        ? "/book/lessonPlan/completedView"
        : "/book/lessonPlan/view";

      const res = await AxiosInstance.post(viewEndpoint, {
        id: Number(lessonPlanId),
        school_id: Number(school_id),
        academic_id: toNumberOrNull(academic_id) ?? academic_id,
      });

      if (res.data?.status && res.data.data) {
        const payload = res.data.data;
        const { Lesson_info = [], item_list = [] } = payload;
        const info = Lesson_info[0] || null;

        const resolvedHmId =
          pickHmId(payload) ?? pickHmId(info) ?? pickHmId(item_list[0]) ?? null;
        setHmId(resolvedHmId);

        const subjectNameFromFile = extractSubjectFromFileName(
          info?.pdf_original_name,
        );

        setLessonInfo(
          info
            ? {
                ...info,
                subject_name: subjectNameFromFile,
                hm_id: resolvedHmId,
              }
            : null,
        );

        const firstItem = item_list[0] || {};
        const startPage = firstItem.start_page ?? "";
        const endPage = firstItem.end_page ?? "";

        setHeaderData({
          book: info?.pdf_original_name?.replace(/\.pdf$/i, "") || "",
          class: info?.class || "",
          subject: subjectNameFromFile || info?.subject || "",
          medium: info?.medium || "",
          topic: info?.topic || "",
          subTopic: info?.sub_topic || "",
          startPage: startPage?.toString() || "",
          endPage: endPage?.toString() || "",
        });

        // ---------- PDF URL (blob-aware) ----------
        const rawPath = info?.uploaded_file_path || "";
        const isBlobPath =
          !!rawPath &&
          !/^https?:\/\//i.test(rawPath) &&
          !rawPath.startsWith("/") &&
          rawPath.includes("/Upload/");
        if (isBlobPath) {
          // Azure blob book — fetch through the authenticated
          // /content/image endpoint and show via an object URL
          try {
            const pdfRes = await AxiosInstance.get("/content/image", {
              params: { path: rawPath },
              responseType: "blob",
            });
            setPdfUrl(
              URL.createObjectURL(
                new Blob([pdfRes.data], { type: "application/pdf" }),
              ),
            );
          } catch (err) {
            console.error("Error loading book PDF from blob:", err);
            setPdfUrl(null);
          }
        } else {
          let fullPdfUrl = null;
          if (rawPath) {
            if (/^https?:\/\//i.test(rawPath)) {
              fullPdfUrl = rawPath;
            } else {
              const base = (IMAGE_BASE_URL || "").replace(/\/+$/, "");
              const path = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
              fullPdfUrl = `${base}${path}`;
            }
          }
          setPdfUrl(fullPdfUrl);
        }
        setFromPage(startPage || null);
        setToPage(endPage || null);

        const plans = item_list.map((item) => ({
          id: item.id,
          lesson_plan_id: item.lesson_plan_id,
          version: item.version,
          content: item.content || "",
          start_page: item.start_page,
          end_page: item.end_page,
          created_at: item.created_at,
          title: `${t.lessonPlan || "Lesson Plan"} ${item.version}`,
          date: item.created_at
            ? new Date(item.created_at).toLocaleDateString()
            : "",
          time: item.created_at
            ? new Date(item.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "",
          status: item.status_label || "Draft",
          hm_pk: item.hm_pk || null,
          hm_id: pickHmId(item) ?? resolvedHmId,
          schedule_date: item.schedule_date || "",
          selected_periods: Array.isArray(item.selected_periods)
            ? item.selected_periods
            : [],
          is_pinned: false,
        }));

        setSavedLessonPlans(plans);

        const pinned = plans.find((p) => p.is_pinned);
        const firstPlan = pinned || plans[0];
        if (firstPlan) {
          setSelectedLessonPlanId(firstPlan.id);
          setLessonPlanContent(firstPlan.content || "");
        } else {
          setSelectedLessonPlanId(null);
          setLessonPlanContent("");
        }
      } else {
        setModal({
          open: true,
          type: "failed",
          message:
            res.data?.message ||
            t.failedToFetchLessonPlan ||
            "Failed to fetch lesson plan",
          navigateAfter: null,
        });
      }
    } catch (err) {
      console.error("Error fetching lesson plan:", err);
      setModal({
        open: true,
        type: "failed",
        message:
          err?.response?.data?.message ||
          t.somethingWentWrong ||
          "Something went wrong",
        navigateAfter: null,
      });
    } finally {
      setLoading(false);
    }
  }, [lessonPlanId, school_id, academic_id, fromPreviousYears, t]);

  useEffect(() => {
    fetchLessonPlan();
  }, [fetchLessonPlan]);

  // ---------- Card handlers ----------
  const handleCardSelect = useCallback(
    (id) => {
      setSelectedLessonPlanId(id);
      const plan = savedLessonPlans.find((lp) => lp.id === id);
      setLessonPlanContent(plan?.content || "");
    },
    [savedLessonPlans],
  );

  const handleCardPin = useCallback((id) => {
    setSavedLessonPlans((prev) =>
      prev.map((lp) =>
        lp.id === id
          ? { ...lp, is_pinned: !lp.is_pinned }
          : { ...lp, is_pinned: false },
      ),
    );
  }, []);

  // ---------- DELETE handlers ----------
  const handleCardDelete = useCallback((id) => {
    setConfirmDelete({ open: true, planId: id });
  }, []);

  const cancelDelete = () => {
    setConfirmDelete({ open: false, planId: null });
  };

  const confirmDeleteAction = useCallback(async () => {
    const idToDelete = confirmDelete.planId;
    if (!idToDelete) return;

    try {
      setDeleting(true);

      const res = await AxiosInstance.delete("/book/lessonPlan/delete", {
        data: {
          // id: Number(lessonPlanId),
          id: "",
          item_id: Number(idToDelete),
          school_id: Number(school_id),
          deleted_by: Number(userId),
        },
      });

      if (res.data?.status) {
        setConfirmDelete({ open: false, planId: null });
        setModal({
          open: true,
          type: "success",
          message:
            res.data?.message ||
            t.successfullyDeleted ||
            "Successfully Deleted",
          navigateAfter: null,
        });
        fetchLessonPlan();
      } else {
        setConfirmDelete({ open: false, planId: null });
        setModal({
          open: true,
          type: "failed",
          message:
            res.data?.message ||
            t.failedToDelete ||
            "Failed to delete lesson plan",
          navigateAfter: null,
        });
      }
    } catch (err) {
      console.error("Error deleting lesson plan:", err);
      setConfirmDelete({ open: false, planId: null });
      setModal({
        open: true,
        type: "failed",
        message:
          err?.response?.data?.message ||
          t.somethingWentWrong ||
          "Something went wrong",
        navigateAfter: null,
      });
    } finally {
      setDeleting(false);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    confirmDelete.planId,
    lessonPlanId,
    school_id,
    userId,
    t,
    fetchLessonPlan,
  ]);

  // ---------- Schedule popup ----------
  const handleCardSchedule = useCallback(
    (id) => {
      const plan = savedLessonPlans.find((lp) => lp.id === id);
      if (!plan) return;

      const enrichedPlan = {
        ...plan,
        subject_name: lessonInfo?.subject_name || "",
        class_name: lessonInfo?.class || "",
        class_id: lessonInfo?.class_id ? Number(lessonInfo.class_id) : null,
        medium: lessonInfo?.medium || "",
        hm_id: plan.hm_id ?? hmId ?? null,
      };

      setSchedulePopup({ open: true, lessonPlan: enrichedPlan });
    },
    [savedLessonPlans, lessonInfo, hmId],
  );

  const handleScheduleCancel = useCallback(() => {
    setSchedulePopup({ open: false, lessonPlan: null });
  }, []);

  const handleScheduleSubmit = useCallback(
    async ({ apiResponse }) => {
      setSchedulePopup({ open: false, lessonPlan: null });
      setModal({
        open: true,
        type: "success",
        message:
          apiResponse?.message ||
          t.lessonPlanScheduledSuccess ||
          "Lesson plan scheduled successfully",
        navigateAfter: null,
      });
      fetchLessonPlan();
    },
    [t, fetchLessonPlan],
  );

  const handleBack = useCallback(
    () => navigate("/user/lessonplanlist"),
    [navigate],
  );

  // Closing the modal optionally redirects (used by the save-to-draft success).
  const closeModal = () => {
    const target = modal.navigateAfter;
    setModal({ open: false, type: "", message: "", navigateAfter: null });
    if (target) navigate(target);
  };

  const handleEdit = (row) => {
    navigate("/user/lessonplancreate", {
      state: { ID: row.lesson_plan_id, data: row, editId: row.id },
    });
  };

  // ---------- SAVE TO DRAFT payloads ----------
  const buildCompletedDraftPayload = useCallback(() => {
    const selectedPlan =
      savedLessonPlans.find((p) => p.id === selectedLessonPlanId) ||
      savedLessonPlans[0] ||
      {};

    const startNum = parseInt(headerData.startPage, 10);
    const endNum = parseInt(headerData.endPage, 10);

    const startPage = !Number.isNaN(startNum)
      ? startNum
      : selectedPlan.start_page != null
        ? Number(selectedPlan.start_page)
        : null;

    const endPage = !Number.isNaN(endNum)
      ? endNum
      : selectedPlan.end_page != null
        ? Number(selectedPlan.end_page)
        : null;

    const subTopic = headerData.subTopic || lessonInfo?.sub_topic || null;

    return {
      school_id: school_id ? Number(school_id) : null,
      academic_id: toNumberOrNull(academic_id_for_completedDraft),
      book_id: toNumberOrNull(lessonInfo?.book_id),
      subject: toNumberOrNull(lessonInfo?.subject),
      class: toNumberOrNull(lessonInfo?.class_id),
      medium: headerData.medium || lessonInfo?.medium || "",
      topic: headerData.topic || lessonInfo?.topic || "",
      sub_topic: subTopic || null,
      start_page: startPage,
      end_page: endPage,
      version: selectedPlan.version || 1,
      content: lessonPlanContent || selectedPlan.content || "",
      created_by: userId,
    };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedLessonPlans,
    selectedLessonPlanId,
    lessonInfo,
    headerData,
    school_id,
    academic_id,
    lessonPlanContent,
    userId,
  ]);

  const handleSaveToDraft = useCallback(async () => {
    if (!lessonPlanContent) {
      setModal({
        open: true,
        type: "failed",
        message:
          t.noLessonPlanContent || "No lesson plan content available to save.",
        navigateAfter: null,
      });
      return;
    }

    const payload = buildCompletedDraftPayload();

    try {
      setSavingDraft(true);
      const res = await AxiosInstance.post(
        "/book/lessonPlan/completedDraft",
        payload,
      );

      if (res.data?.status === true) {
        setModal({
          open: true,
          type: "success",
          message:
            res.data?.message ||
            t.lessonPlanSavedSuccess ||
            "Lesson plan saved to draft successfully",
          navigateAfter: "/user/lessonplanlist",
        });
      } else {
        setModal({
          open: true,
          type: "failed",
          message:
            res.data?.message ||
            t.failedToSaveLessonPlan ||
            "Failed to save lesson plan",
          navigateAfter: null,
        });
      }
    } catch (err) {
      console.error("Error saving lesson plan to draft:", err);
      setModal({
        open: true,
        type: "failed",
        message:
          err?.response?.data?.message ||
          t.somethingWentWrong ||
          "Something went wrong",
        navigateAfter: null,
      });
    } finally {
      setSavingDraft(false);
    }
  }, [lessonPlanContent, buildCompletedDraftPayload, t]);

  // ---------- DOWNLOAD / PRINT lesson plan content as PDF ----------
  const buildPrintableBody = useCallback(() => {
    const esc = (v) => (v == null ? "" : String(v));

    const rows = [
      [t.book || "Book", headerData.book],
      [t.class || "Class", headerData.class],
      [t.subject || "Subject", headerData.subject],
      [t.medium || "Medium", headerData.medium],
      [t.topic || "Topic", headerData.topic],
      [t.subTopic || "Sub Topic", headerData.subTopic],
      [t.startPageLabel || "Start Page", headerData.startPage],
      [t.endPageLabel || "End Page", headerData.endPage],
    ]
      .filter(([, value]) => value != null && value !== "")
      .map(
        ([label, value]) => `
          <tr>
            <td class="lp-key">${esc(label)}</td>
            <td class="lp-val">${esc(value)}</td>
          </tr>`,
      )
      .join("");

    return `
      <div class="lp-doc">
        <div class="lp-header">
          <h2>${esc(t.lessonPlan || "Lesson Plan")}</h2>
        </div>
        <table class="lp-table"><tbody>${rows}</tbody></table>
        <div class="lp-content">${lessonPlanContent || ""}</div>
      </div>
    `;
  }, [headerData, lessonPlanContent, t]);

  const handleDownloadPDF = async () => {
    if (!lessonPlanContent) {
      setModal({
        open: true,
        type: "failed",
        message:
          t.noLessonPlanContent ||
          "No lesson plan content available to download",
        navigateAfter: null,
      });
      return;
    }

    setDownloading(true);

    // Default filename suggested by the browser's "Save as PDF" dialog
    const subjectForName =
      headerData.subject || lessonInfo?.subject_name || "LessonPlan";
    const topicForName = headerData.topic || "";
    const rawName = [subjectForName, topicForName].filter(Boolean).join("_");
    const docTitle =
      rawName.replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "") ||
      "LessonPlan";

    const printHtml = `<!DOCTYPE html>
<html lang="ta">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${docTitle}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #1a1a1a;
      font-family: 'Noto Sans Tamil', 'Latha', 'Nirmala UI', Arial, sans-serif;
    }
    .lp-doc { width: 100%; }
    .lp-header {
      text-align: center;
      border-bottom: 2px solid #e35205;
      padding-bottom: 10px;
      margin-bottom: 16px;
    }
    .lp-header h2 { margin: 0; font-size: 18px; }
    .lp-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin-bottom: 18px;
    }
    .lp-table td {
      padding: 6px 10px;
      border: 1px solid #e0e0e0;
      vertical-align: top;
    }
    .lp-key { font-weight: 600; width: 35%; background: #faf7f5; }
    .lp-content { font-size: 13px; line-height: 1.7; }
    .lp-content img { max-width: 100%; }
    .lp-content table { width: 100%; border-collapse: collapse; }
    .lp-content table td, .lp-content table th {
      border: 1px solid #ccc; padding: 6px 8px;
    }
    h1 { font-size: 18px; }
    h2 { font-size: 16px; }
    h3 { font-size: 14px; }
  </style>
</head>
<body>${buildPrintableBody()}</body>
</html>`;

    // Hidden iframe to host the printable document
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    const cleanup = () => {
      // Remove a little after printing so the dialog has the content
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 500);
    };

    try {
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(printHtml);
      doc.close();

      // Wait for the iframe document to finish loading
      await new Promise((resolve) => {
        if (doc.readyState === "complete") {
          resolve();
        } else {
          iframe.onload = () => resolve();
          // safety timeout
          setTimeout(resolve, 1000);
        }
      });

      // Wait for fonts (Tamil) inside the iframe
      try {
        if (iframe.contentWindow.document.fonts) {
          await iframe.contentWindow.document.fonts.ready;
        }
      } catch (_) {
        /* ignore */
      }

      // Small paint delay
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Clean up after the print dialog is dismissed
      iframe.contentWindow.onafterprint = cleanup;

      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      // Fallback cleanup in case onafterprint doesn't fire
      cleanup();
    } catch (err) {
      console.error("Error printing lesson plan:", err);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      setModal({
        open: true,
        type: "failed",
        message:
          err?.message ||
          t.failedToDownloadPdf ||
          "Failed to generate PDF. Please try again.",
        navigateAfter: null,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {/* Status modal (success / failed) */}
      <PopupModal
        open={modal.open}
        title={
          modal.type === "success"
            ? t.modalSuccessTitle || "Success"
            : t.modalFailedTitle || "Failed"
        }
        message={modal.message}
        onOk={closeModal}
        onClose={closeModal}
      />

      {/* Delete confirmation modal */}
      <PopupModal
        open={confirmDelete.open}
        title={t.delete || "Confirm Delete"}
        message={
          t.confirmDeleteMessage ||
          "Are you sure you want to delete this lesson plan?"
        }
        onOk={confirmDeleteAction}
        onClose={cancelDelete}
        okText={deleting ? t.deleting || "Deleting..." : t.yes || "Yes"}
        cancelText={t.no || "No"}
        showCancel
      />

      {/* Schedule Lesson Plan popup */}
      <PopupModal
        open={schedulePopup.open}
        title={t.scheduleLessonPlan || "Schedule Lesson Plan"}
        onClose={handleScheduleCancel}
        wide
        render
        renderData={
          <ScheduleLessonPlanContent
            lessonPlan={schedulePopup.lessonPlan}
            lessonInfo={lessonInfo}
            onCancel={handleScheduleCancel}
            onSubmit={handleScheduleSubmit}
          />
        }
      />

      {/* ============ TOP: View Lesson Plan Header ============ */}
      <div className={`${styles.container} mb-2`}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaBook />
          </div>
          <h2 className={styles.headerTitle}>
            {t.viewLessonPlan || "View Lesson Plan"}
          </h2>
        </div>

        <div className={styles.viewFormGrid}>
          <CustomTextInput
            label={t.book || "Book"}
            name="book"
            value={headerData.book}
            disabled
          />
          <CustomTextInput
            label={t.class || "Class"}
            name="class"
            value={headerData.class}
            disabled
          />
          <CustomTextInput
            label={t.subject || "Subject"}
            name="subject"
            value={headerData.subject}
            disabled
          />
          <CustomTextInput
            label={t.medium || "Medium"}
            name="medium"
            value={headerData.medium}
            disabled
          />
          <CustomTextInput
            label={t.topic || "Topic"}
            name="topic"
            value={headerData.topic}
            disabled
          />
          <CustomTextInput
            label={t.subTopic || "Sub Topic"}
            name="subTopic"
            value={headerData.subTopic}
            disabled
          />
          <CustomTextInput
            label={t.startPageLabel || "Start Page"}
            name="startPage"
            value={headerData.startPage}
            disabled
          />
          <CustomTextInput
            label={t.endPageLabel || "End Page"}
            name="endPage"
            value={headerData.endPage}
            disabled
          />
        </div>
      </div>

      {/* ============ MIDDLE: Book PDF + Lesson Plan ============ */}
      <div className={`row ${styles.middleRow} mb-2`}>
        <div className="col-lg-6 col-md-6 col-sm-12 col-12">
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={styles.headerIcon}>
                <FaFilePdf />
              </div>
              <h2 className={styles.headerTitle}>
                {t.bookPdfTitle || "Book PDF"}
              </h2>
            </div>
            <div className={styles.pdfWrapper}>
              <PdfViewer
                file={pdfUrl}
                fromPage={fromPage}
                toPage={toPage}
                fallbackMessage={t.pdfFallbackMessage || "PDF will appear here"}
                loadingMessage={t.loading || "Loading..."}
                enableFullscreen
              />
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-6 col-sm-12 col-12">
          <div className={styles.container}>
            {/* Title on the LEFT, Download button on the RIGHT (end to end) */}
            <div
              className={styles.header}
              style={{ justifyContent: "space-between" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div className={styles.headerIcon}>
                  <FaBookOpen />
                </div>
                <h2 className={styles.headerTitle}>
                  {t.lessonPlan || "Lesson Plan"}
                </h2>
              </div>

              <div className={styles.topActions}>
                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  className={styles.downloadButton}
                  disabled={downloading || !lessonPlanContent}
                >
                  <FaFilePdf className={styles.btnIcon} />
                  {downloading
                    ? t.downloading || "Preparing..."
                    : t.downloadPdf || "Download as PDF"}
                </button>
              </div>
            </div>
            <div className={styles.quillWrapper}>
              <QuillEditor
                value={lessonPlanContent}
                onChange={() => {}}
                placeholder={
                  t.lessonPlanEditorPlaceholder ||
                  "Generated lesson plan will appear here..."
                }
                minHeight={400}
                maxHeight={600}
                readOnly
                showWordCount={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ============ BOTTOM: Saved Lesson Plan Cards ============ */}
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h2 className={styles.headerTitle}>
            {t.savedLessonPlan || "Saved Lesson Plan"}
          </h2>
        </div>

        {loading ? (
          <div className={styles.emptyState}>{t.loading || "Loading..."}</div>
        ) : savedLessonPlans.length === 0 ? (
          <div className={styles.emptyState}>
            {t.noSavedLessonPlans || "No saved lesson plans found"}
          </div>
        ) : (
          <div className={`row ${styles.savedCardsRow}`}>
            {savedLessonPlans.map((plan) => (
              <div key={plan.id} className="col-lg-4 col-md-6 col-sm-12 col-12">
                <SavedLessonPlanCard
                  id={plan.id}
                  title={plan.title}
                  date={plan.date}
                  time={plan.time}
                  status={plan.status || "Draft"}
                  isSelected={selectedLessonPlanId === plan.id}
                  isPinned={!!plan.is_pinned}
                  disableScheduleGlobally={disableScheduleGlobally}
                  disableActions={fromPreviousYears}
                  onSelect={handleCardSelect}
                  onPin={handleCardPin}
                  onSchedule={handleCardSchedule}
                  onDelete={handleCardDelete}
                  onEdit={() => handleEdit(plan)}
                  translations={t}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============ FOOTER: Save to Draft (Previous Years) + Back ============ */}
      <div
        className={styles.backButtonWrapper}
        style={{ display: "flex", gap: "12px", justifyContent: "center" }}
      >
        <CustomButton
          className={styles.cancelButton}
          size="sm"
          fullWidth={false}
          onClick={handleBack}
        >
          {t.back || "Back"}
        </CustomButton>

        {/* Save to Draft — only when the user came from Previous Years */}
        {fromPreviousYears && (
          <CustomButton
            className={styles.addButton}
            variant="contained"
            size="sm"
            fullWidth={false}
            onClick={handleSaveToDraft}
            disabled={savingDraft || !lessonPlanContent}
          >
            {savingDraft
              ? t.saving || "Saving..."
              : t.saveToDraft || "Save to Draft"}
          </CustomButton>
        )}
      </div>
    </>
  );
};

export default LessonPlanView;
